// src/components/SurveyForm/Questions/CheckboxQuestion/CheckboxQuestion.jsx

import React, { Fragment } from 'react';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';

/**
 * Componente para preguntas de opción múltiple (checkboxes)
 * Soporta límite de selección, opción "Otro" y validación
 */
const CheckboxQuestion = ({
  questionName,
  description,
  options = [],
  formValue = {},
  otherValue = '',
  questionId,
  index,
  score = null,
  hasError = false,
  submitAttempted = false,
  primaryColor = '#03aae4',
  questionRef,
  onCheckboxChange,
  onOtherInputChange,
  hasOwnBold,
}) => {
  /**
   * Verifica si la opción "Otro" está seleccionada
   */
  const hasOtherSelected = () => {
    const otherOption = options.find((opt) => opt.isOther);
    if (!otherOption) return false;
    return formValue.values?.[otherOption.optionName] === true;
  };

  /**
   * Cuenta cuántas opciones están seleccionadas
   */
  const getSelectedCount = () => {
    return Object.values(formValue.values || {}).filter((v) => v).length;
  };

  /**
   * Verifica si hay un límite de selección activo
   */
  const hasActiveLimit = () => {
    return score !== null && score !== undefined && score > 0;
  };

  /**
   * Determina si un checkbox debe estar deshabilitado
   */
  const isCheckboxDisabled = (isChecked) => {
    const limitActive = hasActiveLimit();
    const selectedCount = getSelectedCount();
    return limitActive && !isChecked && selectedCount >= score;
  };

  /**
   * Verifica si el campo "Otro" tiene error
   */
  const hasOtherFieldError = () => {
    return (
      submitAttempted &&
      hasError &&
      hasOtherSelected() &&
      (!otherValue || otherValue.trim() === '')
    );
  };

  /**
   * Renderiza un checkbox individual
   */
  const renderCheckbox = (option, optionIndex) => {
    const { numberOption, optionName, isOther } = option;

    const isChecked = isOther
      ? hasOtherSelected()
      : formValue.values?.[optionName] || false;

    const disabled = isCheckboxDisabled(isChecked);

    return (
      <FormControlLabel
        ref={optionIndex === 0 ? questionRef : null}
        key={numberOption}
        control={
          <Checkbox
            checked={isChecked}
            name={optionName}
            value={optionName}
            onChange={(event) => onCheckboxChange(event)}
            disabled={disabled}
            sx={{
              color: hasError && submitAttempted ? 'red' : primaryColor,
              '&.Mui-checked': {
                color: primaryColor,
              },
            }}
          />
        }
        label={optionName}
      />
    );
  };

  return (
    <Fragment>
      {/* Título de la pregunta */}
      <FormLabel
        style={{
          fontSize: '1.1em',
          fontWeight: hasOwnBold(questionName) ? 'normal' : 'bold',
          marginBottom: '1.1em',
          color:
            hasError && submitAttempted ? 'red' : 'rgba(0, 0, 0, 0.6)',
        }}
      >
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(questionName) }} />
      </FormLabel>

      {/* Descripción opcional */}
      {description && (
        <Typography
          variant="caption"
          style={{ display: 'block', fontStyle: 'italic' }}
        >
        <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
        </Typography>
      )}

      {/* Grupo de checkboxes */}
      <FormGroup>
        {options.map((option, optionIndex) =>
          renderCheckbox(option, optionIndex)
        )}
      </FormGroup>

      {/* Indicador de límite */}
      {hasActiveLimit() && (
        <Typography
          variant="caption"
          style={{
            display: 'block',
            marginTop: '0.5em',
            color:
              getSelectedCount() >= score
                ? 'error.main'
                : 'rgba(0, 0, 0, 0.6)',
            fontWeight: getSelectedCount() >= score ? 'bold' : 'normal',
          }}
        >
          {getSelectedCount()} / {score} seleccionadas
        </Typography>
      )}

      {/* Campo de texto para "Otro" */}
      {hasOtherSelected() && (
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Especifica tu respuesta..."
          value={otherValue}
          onChange={onOtherInputChange}
          required
          error={hasOtherFieldError()}
          helperText={
            hasOtherFieldError() ? 'Este campo es obligatorio' : ''
          }
          sx={{
            marginTop: '1em',
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: primaryColor,
              },
            },
            '& label.Mui-focused': {
              color: primaryColor,
            },
          }}
        />
      )}

      {/* Mensaje de error general */}
      {hasError && submitAttempted && !hasOtherSelected() && (
        <Typography
          variant="caption"
          style={{
            display: 'block',
            color: 'red',
            marginTop: '0.5em',
            fontWeight: 'bold',
          }}
        >
          Debes seleccionar al menos una opción
        </Typography>
      )}

      <Divider variant="middle" sx={{ marginTop: '1em' }} />
    </Fragment>
  );
};

CheckboxQuestion.propTypes = {
  questionName: PropTypes.string.isRequired,
  description: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      numberOption: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      optionName: PropTypes.string.isRequired,
      isOther: PropTypes.bool,
    })
  ).isRequired,
  formValue: PropTypes.shape({
    values: PropTypes.object,
  }),
  otherValue: PropTypes.string,
  questionId: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  score: PropTypes.number,
  hasError: PropTypes.bool,
  submitAttempted: PropTypes.bool,
  primaryColor: PropTypes.string,
  questionRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  onCheckboxChange: PropTypes.func.isRequired,
  onOtherInputChange: PropTypes.func.isRequired,
};

export default CheckboxQuestion;